import React, { useEffect, useState } from 'react';
import axiosInstance from '../../Services/axiosInstance';
import LoadingSpinner from '../common/LoadingSpinner';
import UsersTable from './UsersTable';
import { Loader2, UserPlus, Sparkles, Users as UsersIcon } from 'lucide-react';
import {
  Card,
  CardContent,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useToaster } from '../common/Toaster';

interface ProfileResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organisation: string;
  role: string;
}

const isDev = process.env.NODE_ENV === 'development';

const generateFakeData = async (): Promise<{ firstName: string; lastName: string; email: string }> => {
  if (isDev) {
    const { faker } = await import('@faker-js/faker');
    return {
      firstName: faker.person.firstName(),
      lastName: faker.person.lastName(),
      email: faker.internet.email(),
    };
  }
  return { firstName: '', lastName: '', email: '' };
};

const Users: React.FC = () => {
  const { notify } = useToaster();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    axiosInstance.get<ProfileResponse>('/api/Profile', { signal: controller.signal })
      .then(res => {
        const role = res.data.role;
        setIsAdmin(role === 'Administrator' || role === 'Admin');
      })
      .catch(() => {
        setIsAdmin(false);
      })
      .finally(() => {
        if (!controller.signal.aborted) setProfileLoading(false);
      });

    return () => controller.abort();
  }, []);

  const handleGenerateFakeData = async () => {
    const data = await generateFakeData();
    setFirstName(data.firstName);
    setLastName(data.lastName);
    setEmail(data.email);
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
  };

  const handleOpenAddUser = () => {
    if (isDev) {
      handleGenerateFakeData();
    } else {
      resetForm();
    }
    setIsAddUserOpen(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    const userData = { firstName, lastName, email };

    try {
      await axiosInstance.post('/api/UserMigration/add-user', userData, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },
      });

      notify('Success', 'User added successfully', 'success');
      setIsAddUserOpen(false);
      resetForm();
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      notify('Error', 'Failed to add user', 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  if (profileLoading) {
    return <LoadingSpinner label="Loading..." />;
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <UsersIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
            <p className="text-sm text-muted-foreground">
              Manage users and migration to Access Workspace
            </p>
          </div>
        </div>
        <Button onClick={handleOpenAddUser} className="gap-2">
          <UserPlus className="h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Users Table (admin only) */}
      {isAdmin && (
        <Card>
          <CardContent className="pt-6">
            <UsersTable refreshKey={refreshKey} />
          </CardContent>
        </Card>
      )}

      {/* Add User Dialog */}
      <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>
              Create a new user for migration to Access Workspace.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="first-name">First Name</Label>
                <Input
                  id="first-name"
                  value={firstName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last-name">Last Name</Label>
                <Input
                  id="last-name"
                  value={lastName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                  placeholder="Enter last name"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                placeholder="Enter email address"
                required
              />
            </div>
            <DialogFooter>
              {isDev && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateFakeData}
                  disabled={isLoading}
                  className="gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Generate Test Data
                </Button>
              )}
              <Button variant="outline" type="button" onClick={() => setIsAddUserOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading} className="gap-2">
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4" />
                )}
                {isLoading ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
